import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model } from '@angular/core';
import moment from 'moment';
import { DateFieldComponent } from '../date-field/date-field.component';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';
import { TimeFieldComponent } from '../time-field/time-field.component';

/** HTML5 formats the native date and time inputs exchange. */
const HTML5_DATE = 'YYYY-MM-DD';
const HTML5_TIME = 'HH:mm';

/** An instant as the two halves that edit it; both empty when the instant is unset. */
export type InstantParts = {
    /** HTML5 date, `yyyy-MM-dd` */
    date: string;
    /** HTML5 time, `HH:mm` */
    time: string;
};

/**
 * @param parts the two halves
 * @returns whether both halves are filled, so the pair denotes an instant
 */
export const isCompleteInstant = (parts: InstantParts): boolean => Boolean(parts.date && parts.time);

/**
 * Splits an ISO instant into the two halves the native inputs bind to, in local time.
 *
 * @param isoTimestamp the instant, or '' when unset
 * @returns the two HTML5 strings, both empty when the instant is unset or unparsable
 */
export const splitInstant = (isoTimestamp: string): InstantParts => {
    const parsed = isoTimestamp ? moment(isoTimestamp) : undefined;
    if (!parsed?.isValid()) {
        return { date: '', time: '' };
    }
    return { date: parsed.format(HTML5_DATE), time: parsed.format(HTML5_TIME) };
};

/**
 * Joins the two edited halves back into an ISO instant, reading them as local time.
 *
 * The time input works to the minute, so an instant that had seconds comes back without them.
 *
 * @param parts the two halves
 * @returns the ISO instant, or '' when either half is missing or the pair does not parse
 */
export const joinInstant = (parts: InstantParts): string => {
    if (!isCompleteInstant(parts)) {
        return '';
    }
    const parsed = moment(`${parts.date}T${parts.time}`, `${HTML5_DATE}T${HTML5_TIME}`, true);
    return parsed.isValid() ? parsed.toISOString() : '';
};

/**
 * One instant as one signal-forms field, edited through a date input and a time input.
 *
 * The value is a single ISO timestamp (empty when unset), which is what makes this a composite
 * rather than two fields side by side: a form model built from these binds one field per instant
 * and therefore has the same shape as the state it edits, so it can be written straight through
 * to a store instead of being copied into a draft and merged back on save.
 *
 * ```html
 * <pace-instant-field [formField]="signalForm.startOfWork" dateAriaLabel="Start date" />
 * ```
 *
 * **A half-typed instant stays here.** Between typing a date and typing a time there is no
 * instant to report, so the field emits `''` — and the two halves are held locally rather than
 * derived from the value, so that `''` coming back cannot wipe the half already typed. This is
 * what lets the value round-trip through a store on every keystroke; deriving the inputs from the
 * value alone would erase the date the moment it was entered. The one visible consequence is that
 * while an instant is half-typed, an external clear leaves the typed half standing — which is the
 * behaviour worth having anyway, since it does not throw away what the user just wrote.
 *
 * The bounds are ISO instants like the value, and only their date halves reach the native picker:
 * a bound taken from a neighbouring value applies to the whole instant, and clamping the time
 * input to it would forbid perfectly good times on every other day. Ordering is the schema's job.
 */
@Component({
    selector: 'pace-instant-field',
    templateUrl: './instant-field.component.html',
    styleUrl: './instant-field.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DateFieldComponent, TimeFieldComponent],
})
export class InstantFieldComponent extends SignalFieldBaseDirective<string> {
    /** the edited instant as an ISO timestamp, '' when unset or only half typed */
    public readonly value = model<string>('');

    /** ISO instants the native date picker is limited to; empty for no limit */
    public readonly minInstant = input('');
    public readonly maxInstant = input('');

    public readonly dateAriaLabel = input('Date');
    public readonly timeAriaLabel = input('Time');

    protected readonly minDate = computed(() => splitInstant(this.minInstant()).date);
    protected readonly maxDate = computed(() => splitInstant(this.maxInstant()).date);

    /**
     * The two halves on show.
     *
     * Re-derived whenever the bound value changes, except for the one case that would destroy
     * input: an incoming `''` while the pair is incomplete is this field's own emission coming
     * back, and re-deriving from it would clear the half that produced it.
     */
    protected readonly parts = linkedSignal<string, InstantParts>({
        source: this.value,
        computation: (isoTimestamp, previous) => {
            const shown = previous?.value;
            if (isoTimestamp === '' && shown && !isCompleteInstant(shown)) {
                return shown;
            }
            return splitInstant(isoTimestamp);
        },
        equal: (one, other) => one.date === other.date && one.time === other.time,
    });

    /**
     * @param date the edited date half
     */
    protected setDate(date: string): void {
        this.report({ ...this.parts(), date });
    }

    /**
     * @param time the edited time half
     */
    protected setTime(time: string): void {
        this.report({ ...this.parts(), time });
    }

    /**
     * Shows the edited pair and reports the instant it denotes.
     *
     * The pair is set first so that the value change it causes finds it already in place.
     *
     * @param parts the edited pair
     */
    private report(parts: InstantParts): void {
        this.parts.set(parts);
        this.value.set(joinInstant(parts));
    }
}
